import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Slider,
  TextField,
  Typography,
  CircularProgress
} from '@mui/material';

interface SeekTimeDialogProps {
  open: boolean;
  onClose: (seekTime: number) => void;
  videoUrl: string;
  videoTitle?: string;
}

export const SeekTimeDialog: React.FC<SeekTimeDialogProps> = ({
  open,
  onClose,
  videoUrl,
  videoTitle = "Set Seek Time"
}) => {
  const [seekTime, setSeekTime] = useState(60); // Default 60 seconds
  const [videoDuration, setVideoDuration] = useState(300); // Default until loaded
  const [isVideoLoading, setIsVideoLoading] = useState(true);
  const [videoError, setVideoError] = useState(false);
  const [currentLoopTime, setCurrentLoopTime] = useState<number>(0); // Track position within 5-second loop
  const [isPlaying, setIsPlaying] = useState(false); // Track if video is playing
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const loopTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const timelineIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Load video metadata to get duration
  const handleVideoLoadedMetadata = useCallback(() => {
    if (videoRef.current) {
      const duration = videoRef.current.duration;
      if (duration && !isNaN(duration) && duration > 0) {
        setVideoDuration(Math.floor(duration));
        setIsVideoLoading(false);
        setVideoError(false);
        console.log(`[SeekTimeDialog] Video duration loaded: ${duration}s`);
        
        // Start the video loop at the current seek time
        startVideoLoop();
      }
    }
  }, []);

  const handleVideoError = useCallback(() => {
    console.error('[SeekTimeDialog] Video failed to load');
    setVideoError(true);
    setIsVideoLoading(false);
  }, []);

  // Start 5-second loop at current seek time
  const startVideoLoop = useCallback(() => {
    if (!videoRef.current || isVideoLoading || videoError) return;

    const video = videoRef.current;
    const startTime = Math.min(seekTime, videoDuration - 5); // Ensure we have 5 seconds left
    
    // Clear any existing loop and timeline
    if (loopTimeoutRef.current) {
      clearTimeout(loopTimeoutRef.current);
    }
    if (timelineIntervalRef.current) {
      clearInterval(timelineIntervalRef.current);
    }

    // Set video to start position and play
    video.currentTime = startTime;
    video.play().then(() => {
      setIsPlaying(true);
      
      // Start timeline tracking
      timelineIntervalRef.current = setInterval(() => {
        if (!video || video.readyState < 2) return;
        
        const currentVideoTime = video.currentTime;
        const relativeTime = currentVideoTime - startTime;
        setCurrentLoopTime(Math.max(0, Math.min(5, relativeTime)));
      }, 100); // Update every 100ms
      
    }).catch(err => {
      console.warn('[SeekTimeDialog] Video play failed:', err);
      setIsPlaying(false);
    });

    // Set up 5-second loop
    loopTimeoutRef.current = setTimeout(() => {
      if (video && !video.paused) {
        video.currentTime = startTime;
        setCurrentLoopTime(0); // Reset timeline
        startVideoLoop(); // Recursive loop
      }
    }, 5000);
  }, [seekTime, videoDuration, isVideoLoading, videoError]);

  // Update video loop when seek time changes
  useEffect(() => {
    if (!isVideoLoading && !videoError) {
      startVideoLoop();
    }
  }, [seekTime, startVideoLoop, isVideoLoading, videoError]);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (loopTimeoutRef.current) {
        clearTimeout(loopTimeoutRef.current);
      }
      if (timelineIntervalRef.current) {
        clearInterval(timelineIntervalRef.current);
      }
    };
  }, []);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setSeekTime(60);
      setIsVideoLoading(true);
      setVideoError(false);
      setIsPlaying(false);
      setCurrentLoopTime(0);
      if (loopTimeoutRef.current) {
        clearTimeout(loopTimeoutRef.current);
      }
      if (timelineIntervalRef.current) {
        clearInterval(timelineIntervalRef.current);
      }
    }
  }, [open]);

  const handleSliderChange = (event: Event, newValue: number | number[]) => {
    const value = Array.isArray(newValue) ? newValue[0] : newValue;
    setSeekTime(Math.floor(value));
  };

  const handleTextInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value, 10);
    if (!isNaN(value) && value >= 0 && value <= videoDuration) {
      setSeekTime(value);
    }
  };

  const handleSave = () => {
    // Clean up video loop and timeline
    if (loopTimeoutRef.current) {
      clearTimeout(loopTimeoutRef.current);
    }
    if (timelineIntervalRef.current) {
      clearInterval(timelineIntervalRef.current);
    }
    if (videoRef.current) {
      videoRef.current.pause();
    }
    
    setIsPlaying(false);
    setCurrentLoopTime(0);
    onClose(seekTime);
  };

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown // Prevent closing without saving
      PaperProps={{
        sx: {
          maxWidth: '600px',
          margin: '20px'
        }
      }}
    >
      <DialogTitle>
        {videoTitle}
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ p: 2 }}>
          {/* Video Player */}
          <Box sx={{ 
            position: 'relative',
            width: '100%',
            height: '300px',
            backgroundColor: '#000',
            borderRadius: 1,
            mb: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {isVideoLoading && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <CircularProgress color="primary" />
                <Typography color="white">Loading video...</Typography>
              </Box>
            )}
            
            {videoError && (
              <Typography color="error" sx={{ color: 'white' }}>
                Failed to load video
              </Typography>
            )}
            
            <video
              ref={videoRef}
              src={videoUrl}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                display: isVideoLoading || videoError ? 'none' : 'block'
              }}
              muted
              playsInline
              onLoadedMetadata={handleVideoLoadedMetadata}
              onError={handleVideoError}
              onLoadStart={() => setIsVideoLoading(true)}
            />
            
            {/* Timeline progress bar showing position in 5-second loop */}
            {videoUrl && isPlaying && !isVideoLoading && !videoError && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: '3px',
                  backgroundColor: 'rgba(0,0,0,0.3)',
                }}
              >
                <Box
                  sx={{
                    height: '100%',
                    width: `${(currentLoopTime / 5) * 100}%`,
                    backgroundColor: '#fff',
                    transition: 'width 0.1s ease-out',
                  }}
                />
              </Box>
            )}
          </Box>

          {/* Seek Time Controls */}
          <Box sx={{ mb: 3 }}>
            <Typography gutterBottom variant="subtitle1" sx={{ fontWeight: 'bold' }}>
              Seek In Time: {seekTime} seconds
            </Typography>
            
            {/* Slider */}
            <Box sx={{ px: 1, mb: 2 }}>
              <Slider
                value={seekTime}
                onChange={handleSliderChange}
                min={0}
                max={videoDuration}
                step={1}
                disabled={isVideoLoading || videoError}
                marks={[
                  { value: 0, label: '0s' },
                  { value: Math.floor(videoDuration / 2), label: `${Math.floor(videoDuration / 2)}s` },
                  { value: videoDuration, label: `${videoDuration}s` }
                ]}
                sx={{
                  '& .MuiSlider-markLabel': {
                    fontSize: '0.75rem'
                  }
                }}
              />
            </Box>

            {/* Text Input */}
            <TextField
              label="Seek Time (seconds)"
              type="number"
              value={seekTime}
              onChange={handleTextInputChange}
              disabled={isVideoLoading || videoError}
              inputProps={{
                min: 0,
                max: videoDuration,
                step: 1
              }}
              size="small"
              sx={{ width: '250px' }}
            />
          </Box>

          <Typography variant="body2" color="text.secondary">
            Preview shows a 5-second loop starting at the selected seek time.
            The default seek time is 60 seconds.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={isVideoLoading}
        >
          Save Seek Time
        </Button>
      </DialogActions>
    </Dialog>
  );
};